import React, { useEffect, useMemo, useRef, useState } from 'react';

import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useQuery } from '@tanstack/react-query';
import DOMPurify from 'dompurify';

import { Button } from '@host/components/NewUI/ComponentsLibrary';

import { contractDownloadUrl, fetchContract } from './api';
import Layout from './components/Layout';
import PortalBackButton from './components/PortalBackButton';
import PortalCard from './components/PortalCard';
import PortalFooter from './components/PortalFooter';
import { palette } from './styles';
import PdfPageSkeleton from '../components/PdfViewer/PdfPageSkeleton';
import PdfViewer, { DEFAULT_PAGE_WIDTH } from '../components/PdfViewer/PdfViewer';
import { onboardQueryKeys } from '../onboardQueryKeys';
import { onboardColors } from '../theme/onboardTokens';

// PdfViewer's inner padding on each side.
const PDF_VIEWER_PADDING = 16;

interface Props {
  token: string;
  offerTitle: string;
  signed: boolean;
  signLocked: boolean;
  onBack: () => void;
  onSign: () => void;
  onDecline: () => void;
}

const OfferPreview: React.FC<Props> = ({
  token,
  offerTitle,
  signed,
  signLocked,

  onBack,
  onSign,
  onDecline
}) => {
  const {
    data: contract,
    isLoading,
    error: queryError
  } = useQuery({
    queryKey: onboardQueryKeys.portalContract(token),
    queryFn: async () => fetchContract(token),
    // Poll every second while the backend is still preparing the contract.
    refetchInterval: (data) => (data?.status === 'preparing' ? 1000 : false),
    // A ready contract only changes when the candidate signs, and signing drops this
    // query (usePortalActions), so reopening the preview needn't re-download the PDF.
    staleTime: Infinity,
    // Always enabled — the contract endpoint is fetched regardless of guard flags.
    enabled: true
  });

  // True while waiting for the first fetch result OR when the backend responded
  // with 202 (contract still rendering).
  const preparing = isLoading || contract?.status === 'preparing';

  // Fit PDF pages to the contract box so phones don't pan sideways across an A4 page.
  const contractBoxRef = useRef<HTMLDivElement>(null);
  const [contractBoxWidth, setContractBoxWidth] = useState<number | null>(null);
  useEffect(() => {
    const box = contractBoxRef.current;
    if (!box) return undefined;
    const observer = new ResizeObserver(([entry]) => setContractBoxWidth(entry.contentRect.width));
    observer.observe(box);
    return () => observer.disconnect();
  }, []);
  const pdfPageWidth = contractBoxWidth
    ? Math.min(DEFAULT_PAGE_WIDTH, contractBoxWidth - PDF_VIEWER_PADDING * 2)
    : undefined;

  const pdfBlob = contract?.status === 'ready' ? contract.pdfBlob : null;
  const rawHtml = contract?.status === 'ready' ? contract.html : null;
  const error = queryError instanceof Error ? queryError.message : String(queryError ?? '');

  // Sanitize the contract HTML client-side before injecting it.
  const html = useMemo(() => {
    if (!rawHtml) return null;
    return DOMPurify.sanitize(rawHtml, {
      ADD_TAGS: ['span', 'img'],
      ADD_ATTR: ['style', 'src', 'alt']
    });
  }, [rawHtml]);

  const downloadContract = () => {
    const link = document.createElement('a');
    link.href = contractDownloadUrl(token);
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const signDisabled = signed || signLocked || preparing || (!pdfBlob && !html);

  const loadingLabel = (() => {
    if (!preparing) return 'Loading contract…';
    return signed ? 'Preparing your signed contract…' : 'Preparing your contract…';
  })();
  // Stays up through preparation, download and PDF rendering (PdfViewer keeps
  // it until the first page paints), so a slow load never shows an empty box.
  const skeleton = <PdfPageSkeleton label={loadingLabel} width={pdfPageWidth} />;

  const signTitle = (() => {
    if (signLocked) return 'Complete pre-offer items first';
    if (preparing) return 'Contract is still being prepared';
    return undefined;
  })();

  return (
    <Layout title={offerTitle || 'Offering letter'}>
      <PortalCard>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            marginBottom: 24
          }}
        >
          <PortalBackButton onClick={onBack} />
          {signed ? (
            <Button
              buttonVariant="secondary"
              icon={<FileDownloadIcon />}
              id="portal-download-contract-button"
              onClick={downloadContract}
            >
              Download
            </Button>
          ) : null}
        </div>
        <div
          ref={contractBoxRef}
          style={{
            border: `1px solid ${palette.divider}`,
            borderRadius: 6,
            padding: error && !pdfBlob && !html ? 24 : 0,
            minHeight: 600,
            background: onboardColors.surface.white,
            overflow: 'auto'
          }}
        >
          {error ? <p style={{ color: palette.warning }}>{error}</p> : null}
          {!pdfBlob && !html && !error ? skeleton : null}
          {pdfBlob ? (
            <PdfViewer data={pdfBlob} placeholder={skeleton} width={pdfPageWidth} />
          ) : null}
          {!pdfBlob && html ? (
            // eslint-disable-next-line react/no-danger
            <div dangerouslySetInnerHTML={{ __html: html }} />
          ) : null}
        </div>
      </PortalCard>

      <PortalFooter>
        {!signed && (
          <Button
            buttonColor="red"
            buttonVariant="secondary"
            id="portal-decline-offer-button"
            onClick={onDecline}
          >
            Decline
          </Button>
        )}
        <Button
          buttonVariant="primary"
          disabled={signDisabled}
          id="portal-sign-offer-button"
          onClick={onSign}
          title={signTitle}
        >
          {signed ? 'Already signed' : 'Sign offer'}
        </Button>
      </PortalFooter>
    </Layout>
  );
};

export default OfferPreview;
